import React from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  Zap, TrendingUp, Shield, BarChart3, Target, Clock,
  ChevronRight, Check, Star, Crown, ArrowRight
} from 'lucide-react';

export const LandingPage: React.FC = () => {
  const { isSignedIn } = useUser();

  return (
    <div className="min-h-screen bg-terminal-bg text-terminal-text font-mono">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-terminal-border bg-terminal-bg/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="h-8 w-8 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded flex items-center justify-center text-black font-black text-xs shadow-lg shadow-cyan-500/20">
                  7S
                </div>
                <div className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-emerald-400 rounded-full animate-pulse" />
              </div>
              <span className="text-terminal-bright font-bold tracking-tight text-lg">
                ALPHA<span className="text-cyan-400">OPTIONS</span>
              </span>
            </div>

            <nav className="flex items-center gap-2 sm:gap-4 md:gap-6">
              <Link to="/guide" className="hidden sm:block text-terminal-dim hover:text-white text-sm transition-colors">
                How to Use
              </Link>
              <Link to="/pricing" className="hidden sm:block text-terminal-dim hover:text-white text-sm transition-colors">
                Pricing
              </Link>
              {isSignedIn ? (
                <>
                  <Link to="/dashboard" className="hidden md:block text-terminal-dim hover:text-white text-sm transition-colors">
                    Dashboard
                  </Link>
                  <Link
                    to="/scanner"
                    className="px-3 sm:px-4 py-2 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black rounded-lg font-bold text-xs sm:text-sm hover:opacity-90 whitespace-nowrap"
                  >
                    Scanner
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-terminal-dim hover:text-white text-xs sm:text-sm transition-colors whitespace-nowrap">
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    className="px-3 sm:px-4 py-2 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black rounded-lg font-bold text-xs sm:text-sm hover:opacity-90 whitespace-nowrap"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 sm:py-32">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 via-transparent to-transparent" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm mb-8">
              <Zap className="w-4 h-4" />
              <span>Institutional-Grade Options Analysis</span>
            </div>

            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
              Your Market Edge,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">
                Decoded.
              </span>
            </h1>

            <p className="text-xl text-terminal-dim max-w-2xl mx-auto mb-10 leading-relaxed">
              Scan any stock with our 7-Step Framework. Get probability-weighted trade setups,
              real-time Greeks, and complete trade plans in seconds.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to={isSignedIn ? "/scanner" : "/signup"}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black rounded-xl font-bold text-lg hover:opacity-90 transition-opacity shadow-lg shadow-cyan-500/20"
              >
                {isSignedIn ? 'Open Scanner' : 'Start Free Trial'}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-terminal-card border border-terminal-border text-white rounded-xl font-bold text-lg hover:bg-slate-800 transition-colors"
              >
                View Pricing
              </Link>
            </div>

            {/* Stats */}
            <div className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-2xl mx-auto">
              <div>
                <div className="text-2xl sm:text-3xl font-black text-white">7-Step</div>
                <div className="text-terminal-dim text-sm sm:text-base">Analysis Framework</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-black text-white">Real-time</div>
                <div className="text-terminal-dim text-sm sm:text-base">Market Data</div>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-black text-white">Instant</div>
                <div className="text-terminal-dim text-sm sm:text-base">Trade Plans</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 border-t border-terminal-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              The 7-Step Framework
            </h2>
            <p className="text-terminal-dim max-w-2xl mx-auto">
              Our proprietary analysis engine evaluates every trade through a systematic,
              probability-weighted framework used by institutional traders.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { icon: <TrendingUp className="w-6 h-6" />, title: 'Sentiment Analysis', desc: 'Sector strength & VIX environment' },
              { icon: <BarChart3 className="w-6 h-6" />, title: 'Technical Setup', desc: 'RSI, MACD, EMA alignment' },
              { icon: <Shield className="w-6 h-6" />, title: 'Risk Assessment', desc: 'Earnings, FOMC, IV analysis' },
              { icon: <Target className="w-6 h-6" />, title: 'Position Sizing', desc: 'VIX-adjusted recommendations' },
            ].map((feature, idx) => (
              <div key={idx} className="bg-terminal-card rounded-xl border border-terminal-border p-6 hover:border-cyan-500/30 transition-colors">
                <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center text-cyan-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-white font-bold mb-2">{feature.title}</h3>
                <p className="text-terminal-dim text-base">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 border-t border-terminal-border bg-gradient-to-b from-transparent to-cyan-500/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-terminal-dim">Choose the plan that fits your trading style</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Basic Plan */}
            <div className="bg-terminal-card rounded-2xl border border-terminal-border p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-cyan-500/10 rounded-lg text-cyan-400">
                  <Zap className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white">Basic</h3>
              </div>
              <div className="mb-6">
                <span className="text-3xl sm:text-4xl font-black text-white">£19.99</span>
                <span className="text-terminal-dim">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['5 scans per day', '7-Step Analysis', 'Real-time data', 'Trade plans'].map((feature) => (
                  <li key={feature} className="flex items-center gap-3 text-terminal-text">
                    <Check className="w-5 h-5 text-emerald-400" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link
                to="/pricing"
                className="block w-full py-3 text-center bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-lg font-bold hover:bg-cyan-500/20 transition-colors"
              >
                Get Started
              </Link>
            </div>

            {/* Elite Plan */}
            <div className="bg-terminal-card rounded-2xl border border-amber-500/30 p-8 relative ring-2 ring-amber-500/20">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-amber-500 to-amber-600 text-black text-xs font-bold rounded-full">
                  <Star className="w-3 h-3" /> POPULAR
                </span>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-amber-500/10 rounded-lg text-amber-400">
                  <Crown className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white">Elite</h3>
              </div>
              <div className="mb-6">
                <span className="text-3xl sm:text-4xl font-black text-white">£29.99</span>
                <span className="text-terminal-dim">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['50 scans per day', '7-Step Analysis', 'Real-time data', 'Trade management advice'].map((feature) => (
                  <li key={feature} className="flex items-center gap-3 text-terminal-text">
                    <Check className="w-5 h-5 text-emerald-400" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link
                to="/pricing"
                className="block w-full py-3 text-center bg-gradient-to-r from-amber-500 to-amber-600 text-black rounded-lg font-bold hover:opacity-90 transition-opacity"
              >
                Get Elite Access
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 border-t border-terminal-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Trade Smarter?
          </h2>
          <p className="text-terminal-dim text-lg mb-8 max-w-2xl mx-auto">
            Join traders who use Alpha Options to find high-probability setups with defined risk.
          </p>
          <Link
            to={isSignedIn ? "/scanner" : "/signup"}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black rounded-xl font-bold text-lg hover:opacity-90 transition-opacity"
          >
            {isSignedIn ? 'Start Scanning' : 'Create Free Account'}
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-terminal-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded flex items-center justify-center text-black font-black text-xs">
                7S
              </div>
              <span className="text-terminal-bright font-bold">
                ALPHA<span className="text-cyan-400">OPTIONS</span>
              </span>
            </div>

            <div className="flex items-center gap-6 text-sm text-terminal-dim">
              <Link to="/guide" className="hover:text-white transition-colors">How to Use</Link>
              <span>•</span>
              <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
              <span>•</span>
              <Link to="/login" className="hover:text-white transition-colors">Sign In</Link>
            </div>

            <div className="text-terminal-dim text-sm text-center md:text-right">
              <p>© 2026 Alpha Options. All rights reserved.</p>
              <p className="mt-1">Not financial advice. Trade responsibly.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
